import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { gsap, MOTION_QUERIES, useGSAP } from '@/lib/motion'

type Reveal = 'up' | 'down' | 'left' | 'right' | 'center' | 'none'

interface ParallaxImageProps {
    src: string
    alt: string
    className?: string
    imageClassName?: string
    amount?: number
    reveal?: Reveal
    priority?: boolean
}

const HIDDEN: Record<Exclude<Reveal, 'none'>, string> = {
    up: 'inset(100% 0% 0% 0%)',
    down: 'inset(0% 0% 100% 0%)',
    left: 'inset(0% 100% 0% 0%)',
    right: 'inset(0% 0% 0% 100%)',
    center: 'inset(50% 50% 50% 50%)'
}

// An image that drifts inside its frame while the page scrolls, and opens with
// a clip-path wipe the first time it is seen. The frame keeps its own size, so
// give it an aspect ratio or a height through className.
const ParallaxImage: FC<ParallaxImageProps> = ({
    src,
    alt,
    className,
    imageClassName,
    amount = 14,
    reveal = 'up',
    priority = false
}): ReactNode => {
    const frameRef = useRef<HTMLDivElement>(null)
    const imageRef = useRef<HTMLImageElement>(null)

    useGSAP(() => {
        const frame = frameRef.current
        const image = imageRef.current

        if (!frame || !image) return

        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.motion, () => {
            gsap.fromTo(image, { yPercent: -amount / 2 }, {
                yPercent: amount / 2,
                ease: 'none',
                scrollTrigger: {
                    trigger: frame,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true
                }
            })

            if (reveal !== 'none') {
                gsap.fromTo(frame, { clipPath: HIDDEN[reveal] }, {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    duration: 1.35,
                    ease: 'expo.inOut',
                    scrollTrigger: {
                        trigger: frame,
                        start: 'top 88%',
                        once: true
                    }
                })
            }
        })

        return () => mm.revert()
    }, { scope: frameRef })

    return (
        <div ref={frameRef} className={cn('relative overflow-hidden bg-fore/5', className)}>
            <img
                ref={imageRef}
                src={src}
                alt={alt}
                loading={priority ? 'eager' : 'lazy'}
                decoding='async'
                className={cn('absolute inset-x-0 -top-[8%] h-[116%] w-full object-cover', imageClassName)}
            />
        </div>
    )
}

export default ParallaxImage
